import { EventEmitter } from "events";

import { EditOperationBuilder, StringFileData } from "components/organisms/manuscript/adapter/editor-core";

import EditorWatchdogManager from "components/organisms/manuscript/overleaf/ot/editorWatchdogManager";
import sharejs, { Doc } from "components/organisms/manuscript/overleaf/sharejs/vendor/libs/sharejs";
import { historyOTType } from "components/organisms/manuscript/adapter/ot";
import { debugConsole, getMeta } from "components/organisms/manuscript/overleaf/sharejs/vendor/support";

const SINGLE_USER_FLUSH_DELAY = 2000;
const MULTI_USER_FLUSH_DELAY = 500;
const INFLIGHT_OP_TIMEOUT = 5000;
const WAIT_FOR_CONNECTION_TIMEOUT = 500;
const FATAL_OP_TIMEOUT = 45000;
const RECENT_ACK_LIMIT = 2 * SINGLE_USER_FLUSH_DELAY;

function decodeUtf8(value) {
    return typeof value === "string" ? value : value;
}

function normalizeSnapshot(docLines, type) {
    if (type === "history-ot") {
        if (docLines instanceof StringFileData) {
            return docLines;
        }
        return StringFileData.fromRaw(docLines);
    }

    if (Array.isArray(docLines)) {
        return docLines.map((line) => decodeUtf8(line)).join("\n");
    }

    return typeof docLines === "string" ? docLines : "";
}

function normalizeType(type) {
    return type === "history-ot" ? historyOTType : sharejs.types.text;
}

function resolveShareJsDocOptions(maybeOptions, maybeEventEmitter, maybeType) {
    if (
        maybeOptions == null ||
        (typeof maybeOptions === "object" &&
            !Array.isArray(maybeOptions) &&
            (Object.prototype.hasOwnProperty.call(maybeOptions, "type") ||
                Object.prototype.hasOwnProperty.call(maybeOptions, "maxDocLength") ||
                Object.prototype.hasOwnProperty.call(maybeOptions, "globalEditorWatchdogManager") ||
                Object.prototype.hasOwnProperty.call(maybeOptions, "eventEmitter")))
    ) {
        return maybeOptions || {};
    }

    return {
        globalEditorWatchdogManager: maybeOptions,
        eventEmitter: maybeEventEmitter,
        type: typeof maybeType === "string" ? maybeType : "sharejs-text-ot",
    };
}

export class ShareJsDoc extends EventEmitter {
    constructor(docId, docLines, version, socket, maybeOptions = {}, maybeEventEmitter, maybeType) {
        super();
        const options = resolveShareJsDocOptions(maybeOptions, maybeEventEmitter, maybeType);
        this.doc_id = docId;
        this.socket = socket;
        this.type = options.type || "sharejs-text-ot";
        this.maxDocLength = options.maxDocLength;
        this.globalEditorWatchdogManager = options.globalEditorWatchdogManager || null;
        this.eventEmitter = options.eventEmitter || null;
        this.editorWatchdogManager = new EditorWatchdogManager({
            parent: this.globalEditorWatchdogManager,
        });
        this.detachEditorWatchdogManager = null;
        this.track_changes = false;
        this.track_changes_id_seeds = null;
        this.lastAcked = null;
        this.pendingOpCreatedAt = null;
        this.inflightOpCreatedAt = null;
        this.queuedMessageTimer = null;
        this.queuedMessages = [];
        this._timeoutTimer = null;

        this.connection = {
            send: (update) => {
                if (!update.meta) {
                    update.meta = {};
                }
                if (!update.meta.source) {
                    update.meta.source = this.connection.id;
                }
                if (this.track_changes && this.track_changes_id_seeds?.inflight) {
                    update.meta.tc = this.track_changes_id_seeds.inflight;
                }
                this.startInflightOpTimeout(update);
                return this.socket.emit("applyOtUpdate", this.doc_id, update, (error) => {
                    if (error != null) {
                        this.handleError(error);
                    }
                });
            },
            state: "ok",
            id: this.socket.publicId,
        };

        this._doc = new Doc(this.connection, this.doc_id, {
            type: normalizeType(this.type),
        });
        this._doc.setFlushDelay(SINGLE_USER_FLUSH_DELAY);

        this._doc.on("change", (...args) => {
            const isRemote = args[3];
            if (!isRemote && !this.pendingOpCreatedAt) {
                debugConsole.log("set pendingOpCreatedAt", new Date());
                this.pendingOpCreatedAt = performance.now();
            }
            this.trigger("change", ...args);
        });

        this._doc.on("acknowledge", (...args) => {
            this.lastAcked = performance.now();
            this.inflightOpCreatedAt = null;
            debugConsole.log("unset inflightOpCreatedAt");
            this.editorWatchdogManager.onAck();
            this.trigger("acknowledge", ...args);
        });

        this._doc.on("remoteop", (...args) => {
            this._doc.setFlushDelay(MULTI_USER_FLUSH_DELAY);
            this.trigger("remoteop", ...args);
        });

        this._doc.on("flipped_pending_to_inflight", (...args) => {
            this.inflightOpCreatedAt = this.pendingOpCreatedAt;
            debugConsole.log("set inflightOpCreatedAt from pendingOpCreatedAt");
            this.pendingOpCreatedAt = null;
            debugConsole.log("unset pendingOpCreatedAt");
            this.trigger("flipped_pending_to_inflight", ...args);
        });

        this._doc.on("saved", (...args) => {
            this.trigger("saved", ...args);
        });

        this._doc.on("ranges:dirty", (...args) => {
            this.trigger("ranges:dirty", ...args);
        });

        this._doc.on("error", (error) => {
            this.handleError(error);
        });

        this.bindToDocChanges(this._doc);

        this.processUpdateFromServer({
            open: true,
            v: version,
            snapshot: normalizeSnapshot(docLines, this.type),
        });
        this.removeCarriageReturnCharFromShareJsDoc();
    }

    trigger(...args) {
        return this.emit(...args);
    }

    setTrackChangesUserId(userId) {
        this.track_changes = userId != null;
    }

    getTrackedChanges() {
        if (this._doc.otType === "history-ot") {
            return this._doc.snapshot.getTrackedChanges();
        }
        return null;
    }

    getComments() {
        if (this._doc.otType === "history-ot") {
            return this._doc.snapshot.getComments();
        }
        return null;
    }

    submitOp(op) {
        this._doc.submitOp(op);
    }

    removeCarriageReturnCharFromShareJsDoc() {
        let nextPos = this._doc.getText().indexOf("\r");

        while (nextPos !== -1) {
            debugConsole.log("[ShareJsDoc] remove-carriage-return-char", nextPos);
            this._doc.del(nextPos, 1);
            nextPos = this._doc.getText().indexOf("\r");
        }
    }

    isAheadOfExpectedVersion(message) {
        return this._doc.version > 0 && message.v > this._doc.version;
    }

    pushOntoQueue(message) {
        debugConsole.log(`[processUpdate] push onto queue ${message.v}`);
        if (!this.queuedMessageTimer) {
            this.queuedMessageTimer = window.setTimeout(() => {
                debugConsole.log(`[processUpdate] queue timeout fired for ${message.v}`);
                this.processUpdateFromServer(message);
            }, INFLIGHT_OP_TIMEOUT);
        }

        this.queuedMessages.push(message);
        this.queuedMessages.sort((left, right) => left.v - right.v);
    }

    clearQueue() {
        this.queuedMessages = [];
    }

    processQueue() {
        if (!this.queuedMessages.length) {
            return;
        }

        const nextAvailableVersion = this.queuedMessages[0].v;
        if (nextAvailableVersion > this._doc.version) {
            return;
        }

        debugConsole.log(`[processUpdate] taken from queue ${nextAvailableVersion}`);
        const message = this.queuedMessages.shift();
        if (message) {
            this.processUpdateFromServerInOrder(message);
        }

        if (!this.queuedMessages.length && this.queuedMessageTimer) {
            debugConsole.log("[processUpdate] queue is empty, cleared timeout");
            window.clearTimeout(this.queuedMessageTimer);
            this.queuedMessageTimer = null;
        }
    }

    processUpdateFromServerInOrder(message) {
        if (this.isAheadOfExpectedVersion(message)) {
            this.pushOntoQueue(message);
            return;
        }

        const error = this.processUpdateFromServer(message);
        if (error instanceof Error && error.message === "Invalid version from server") {
            this.clearQueue();
            return;
        }

        this.processQueue();
    }

    processUpdateFromServer(message) {
        try {
            if (this.type === "history-ot" && message.op != null) {
                const trackedChangesBefore = this._doc.snapshot.getTrackedChanges();
                const commentsBefore = this._doc.snapshot.getComments();
                this._doc._onMessage({
                    ...message,
                    op: message.op.map((operation) =>
                        typeof operation?.apply === "function" ? operation : EditOperationBuilder.fromJSON(operation),
                    ),
                });
                if (
                    this.rangesUpdated(
                        trackedChangesBefore,
                        commentsBefore,
                        this._doc.snapshot.getTrackedChanges(),
                        this._doc.snapshot.getComments(),
                    )
                ) {
                    this.trigger("ranges:dirty");
                }
            } else {
                this._doc._onMessage(message);
            }
        } catch (error) {
            debugConsole.log(error);
            this.handleError(error);
            return error;
        }

        if (message.meta?.type === "external") {
            this.trigger("externalUpdate", message);
        }

        return undefined;
    }

    catchUp(updates = []) {
        return updates.map((update) =>
            this.processUpdateFromServer({
                ...update,
                v: this._doc.version,
                doc: this.doc_id,
            }),
        );
    }

    getSnapshot() {
        return this._doc.getText();
    }

    getRawSnapshot() {
        if (this.type !== "history-ot") {
            return this.getSnapshot();
        }
        return this._doc.snapshot?.toRaw?.() || { content: this._doc.getText() };
    }

    getVersion() {
        return this._doc.version;
    }

    getTimeSinceLastServerActivity() {
        return Math.floor(performance.now() - this._doc.lastServerActivity);
    }

    getType() {
        return this.type;
    }

    clearInflightAndPendingOps() {
        this.clearFatalTimeoutTimer();
        this._doc.inflightOp = null;
        this._doc.inflightCallbacks = [];
        this._doc.pendingOp = null;
        this._doc.pendingCallbacks = [];
    }

    flushPendingOps() {
        return this._doc.flush();
    }

    updateConnectionState(state) {
        debugConsole.log(`[updateConnectionState] Setting state to ${state}`);
        this.connection.state = state;
        this.connection.id = this.socket.publicId;
        this._doc.autoOpen = false;
        this._doc._connectionStateChanged(state);
        this.lastAcked = null;
    }

    hasBufferedOps() {
        return this._doc.inflightOp != null || this._doc.pendingOp != null;
    }

    getInflightOp() {
        return this._doc.inflightOp;
    }

    getPendingOp() {
        return this._doc.pendingOp;
    }

    getRecentAck() {
        return this.lastAcked !== null && performance.now() - this.lastAcked < RECENT_ACK_LIMIT;
    }

    getInflightOpCreatedAt() {
        return this.inflightOpCreatedAt;
    }

    getPendingOpCreatedAt() {
        return this.pendingOpCreatedAt;
    }

    attachToCM6(cm6) {
        this.attachToEditor(cm6, () => {
            const maxDocLength = this.maxDocLength ?? getMeta("ol-maxDocLength");
            if (typeof cm6.attachShareJs === "function") {
                cm6.attachShareJs(this._doc, maxDocLength);
                return;
            }
            cm6.attachShareDoc?.(this._doc, maxDocLength);
        });
    }

    detachFromCM6() {
        this.maybeDetachEditorWatchdogManager();
        this._doc.detach_cm6?.();
    }

    attachToEditor(editor, attachToShareJs) {
        this.attachEditorWatchdogManager(editor);
        attachToShareJs();
    }

    attachEditorWatchdogManager(editor) {
        this.detachEditorWatchdogManager = this.editorWatchdogManager.attachToEditor(editor);
    }

    maybeDetachEditorWatchdogManager() {
        if (typeof this.detachEditorWatchdogManager === "function") {
            this.detachEditorWatchdogManager();
        }
        this.detachEditorWatchdogManager = null;
    }

    startInflightOpTimeout(update) {
        this.startFatalTimeoutTimer(update);

        const retryOp = () => {
            debugConsole.log("[inflightOpTimeout] Trying op again");
            if (this._doc.inflightOp == null) {
                return;
            }

            update.dupIfSource = [this.connection.id, ...Array.from(this._doc.inflightSubmittedIds)];

            if (this.connection.state !== "ok") {
                debugConsole.log("[inflightOpTimeout] Not connected, retrying in 0.5s");
                window.setTimeout(retryOp, WAIT_FOR_CONNECTION_TIMEOUT);
                return;
            }

            debugConsole.log("[inflightOpTimeout] Sending");
            this.connection.send(update);
        };

        const timer = window.setTimeout(retryOp, INFLIGHT_OP_TIMEOUT);
        this._doc.inflightCallbacks.push(() => {
            this.clearFatalTimeoutTimer();
            window.clearTimeout(timer);
        });
    }

    startFatalTimeoutTimer(update) {
        if (this._timeoutTimer != null) {
            return;
        }

        this._timeoutTimer = window.setTimeout(() => {
            this.clearFatalTimeoutTimer();
            this.trigger("op:timeout", update);
        }, FATAL_OP_TIMEOUT);
    }

    clearFatalTimeoutTimer() {
        if (this._timeoutTimer == null) {
            return;
        }

        clearTimeout(this._timeoutTimer);
        this._timeoutTimer = null;
    }

    handleError(error, meta = {}) {
        this.trigger("error", error, meta);
    }

    bindToDocChanges(doc) {
        const submitOp = doc.submitOp;
        doc.submitOp = (op, callback) => {
            this.recordDocumentFirstChangeEvent();
            this.trigger("op:sent", op);
            doc.pendingCallbacks.push(() => {
                this.trigger("op:acknowledged", op);
            });
            if (this.type === "history-ot") {
                const trackedChangesBefore = doc.snapshot.getTrackedChanges();
                const commentsBefore = doc.snapshot.getComments();
                const result = submitOp.call(doc, op, callback);
                if (
                    this.rangesUpdated(
                        trackedChangesBefore,
                        commentsBefore,
                        doc.snapshot.getTrackedChanges(),
                        doc.snapshot.getComments(),
                    )
                ) {
                    this.trigger("ranges:dirty");
                }
                return result;
            }
            return submitOp.call(doc, op, callback);
        };

        const flush = doc.flush;
        doc.flush = () => {
            this.trigger("flush", doc.inflightOp, doc.pendingOp, doc.version);
            return flush.call(doc);
        };
    }

    recordDocumentFirstChangeEvent() {
        if (typeof this.eventEmitter?.emit === "function") {
            this.eventEmitter.emit("document:first-change", {
                doc_id: this.doc_id,
            });
        }
    }

    rangesUpdated(trackedChangesBefore, commentsBefore, trackedChangesAfter, commentsAfter) {
        return (
            trackedChangesBefore.length !== trackedChangesAfter.length ||
            commentsBefore.length !== commentsAfter.length ||
            this.itemsChanged(trackedChangesBefore.asSorted(), trackedChangesAfter.asSorted()) ||
            this.itemsChanged(commentsBefore.toArray?.() || [], commentsAfter.toArray?.() || [])
        );
    }

    itemsChanged(before, after) {
        for (let i = 0; i < before.length; i += 1) {
            if (before[i] !== after[i]) {
                return true;
            }
        }
        return false;
    }
}

export default ShareJsDoc;
